import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import UploadModal from "../components/modals/UploadModal";
import ApiService from "../services/ApiService";

interface ActionCard {
    title: string;
    description: string;
    buttonText: string;
    buttonVariant: string;
    onClick: () => void;
}

interface HomeProps {
    apiService: ApiService;
    maxFileSizeBytes?: number;
}

const Home: React.FC<HomeProps> = (props) => {
    const { apiService, maxFileSizeBytes } = props;
    const { t } = useTranslation(["home", "common"]);
    const navigate = useNavigate();

    const [showUploadModal, setShowUploadModal] = React.useState(false);

    const handleUploadSuccess = () => {
        // Navigate to templates list to show the new template
        navigate("/templates");
    };

    const actionCards: ActionCard[] = [
        {
            title: t("actionCards.createDocuments.title"),
            description: t("actionCards.createDocuments.description"),
            buttonText: t("actionCards.createDocuments.buttonText"),
            buttonVariant: "primary",
            onClick: () => navigate("/documents/create"),
        },
        {
            title: t("actionCards.viewDocuments.title"),
            description: t("actionCards.viewDocuments.description"),
            buttonText: t("actionCards.viewDocuments.buttonText"),
            buttonVariant: "outline-primary",
            onClick: () => navigate("/documents"),
        },
        {
            title: t("actionCards.uploadTemplate.title"),
            description: t("actionCards.uploadTemplate.description"),
            buttonText: t("actionCards.uploadTemplate.buttonText"),
            buttonVariant: "secondary",
            onClick: () => setShowUploadModal(true),
        },
        {
            title: t("actionCards.viewTemplates.title"),
            description: t("actionCards.viewTemplates.description"),
            buttonText: t("actionCards.viewTemplates.buttonText"),
            buttonVariant: "outline-secondary",
            onClick: () => navigate("/templates"),
        },
    ];

    return (
        <Container fluid>
            <div className="mb-5">
                <h1 className="display-4 mb-3">{t("common:base.title")}</h1>
                <p className="lead text-muted">{t("hero.subtitle")}</p>
            </div>

            <Row className="g-4">
                {actionCards.map((card, index) => (
                    <Col md={6} lg={3} key={index}>
                        <Card
                            className="h-100 shadow-sm action-card home-action-card"
                            style={{ transition: "transform 0.2s" }}
                        >
                            <Card.Body className="d-flex flex-column">
                                <Card.Title className="mb-3">{card.title}</Card.Title>
                                <Card.Text className="flex-grow-1 text-muted">{card.description}</Card.Text>
                                <Button variant={card.buttonVariant} onClick={card.onClick} className="mt-auto">
                                    {card.buttonText}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            <div className="mt-5 pt-4 border-top">
                <Row>
                    <Col md={8}>
                        <h2>{t("quickStart.title")}</h2>
                        <ol className="text-muted">
                            <li>
                                <Trans
                                    i18nKey="quickStart.steps.template"
                                    ns="home"
                                    components={[
                                        <a
                                            href="https://www.adobe.com/acrobat/hub/create-fillable-pdf-from-word-document.html"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Adobe Acrobat
                                        </a>,
                                        <a
                                            href="https://www.sejda.com/de/pdf-forms"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Sejda
                                        </a>,
                                    ]}
                                />
                            </li>
                            <li>{t("quickStart.steps.upload")}</li>
                            <li>{t("quickStart.steps.create")}</li>
                            <li>{t("quickStart.steps.event")}</li>
                            <li>{t("quickStart.steps.map")}</li>
                            <li>{t("quickStart.steps.generate")}</li>
                        </ol>
                    </Col>
                </Row>
            </div>

            <UploadModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                apiService={apiService}
                onSuccess={handleUploadSuccess}
                maxFileSizeBytes={maxFileSizeBytes}
            />

            <style>{`
                .home-action-card:hover {
                    transform: translateY(-4px);
                }
            `}</style>
        </Container>
    );
};

export default Home;
